import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import { SectionHeader } from "../../../components/GlobalComponents/SectionHeader/SectionHeader";
import Icon from "../../../components/Icon/Icon";
import { useBuilderDims } from "../../../components/Exports/Builder/hooks/useBuilderDims";
import { materializeView } from "../../../components/Exports/Builder/utils/materializeView";
import { deserializeView } from "../../../components/Exports/Builder/utils/viewState";
import {
  useCustomDashboard,
  useCustomDashboardMutations,
} from "./hooks/useCustomDashboards";
import {
  normalizeContent,
  emptySlide,
  defaultPieceLayout,
  uid,
  SLIDE_MAX_ROWS,
} from "./utils/dashboardContent";
import { freezePieceResult } from "./utils/pivotSnapshot";
import DashboardGrid from "./components/DashboardGrid";
import PieceEditor from "./components/PieceEditor";

export const CustomDashboardBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useCustomDashboard(id);
  const { update } = useCustomDashboardMutations();
  const { dimByKey, fieldTitle, labelsRef } = useBuilderDims();

  const [name, setName] = useState("");
  const [type, setType] = useState("one_page");
  const [slides, setSlides] = useState([]);
  const [slideIdx, setSlideIdx] = useState(0);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPiece, setEditingPiece] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [dirty, setDirty] = useState(false);

  // Hydrate local editable state once the dashboard loads.
  useEffect(() => {
    if (!data) return;
    const content = normalizeContent(data.content, data.type);
    setName(data.name || "");
    setType(data.type || "one_page");
    setSlides(content.slides);
    setSlideIdx(0);
    setDirty(false);
  }, [data]);

  const isSlides = type === "slides";
  const current = slides[Math.min(slideIdx, slides.length - 1)] || slides[0];

  const patchSlides = (updater) => {
    setSlides((prev) => updater(prev));
    setDirty(true);
  };

  const updateCurrentItems = (mapItems) =>
    patchSlides((prev) =>
      prev.map((s, i) =>
        i === slideIdx ? { ...s, items: mapItems(s.items) } : s
      )
    );

  const handleLayoutChange = (layout) => {
    const byId = Object.fromEntries(layout.map((l) => [l.id, l]));
    updateCurrentItems((items) =>
      items.map((it) =>
        byId[it.id]
          ? { ...it, layout: { x: byId[it.id].x, y: byId[it.id].y, w: byId[it.id].w, h: byId[it.id].h } }
          : it
      )
    );
  };

  const handleAddPiece = () => {
    setEditingPiece(null);
    setEditorOpen(true);
  };

  const handleEditPiece = (piece) => {
    setEditingPiece(piece);
    setEditorOpen(true);
  };

  const handleRemovePiece = (piece) => {
    if (!window.confirm("¿Eliminar esta pieza?")) return;
    updateCurrentItems((items) => items.filter((it) => it.id !== piece.id));
  };

  const handleRenamePiece = (piece, newTitle) => {
    updateCurrentItems((items) =>
      items.map((it) => (it.id === piece.id ? { ...it, title: newTitle } : it))
    );
  };

  const handleSavePiece = (built) => {
    // PieceEditor may return a single piece (chart/table/one indicator) or an
    // array (several indicators picked at once via the tags multi-select).
    const list = Array.isArray(built) ? built : [built];
    if (!list.length) {
      setEditorOpen(false);
      setEditingPiece(null);
      return;
    }
    if (editingPiece) {
      const b = list[0]; // editing updates the single piece in place
      updateCurrentItems((items) =>
        items.map((it) => (it.id === editingPiece.id ? { ...it, ...b } : it))
      );
    } else {
      updateCurrentItems((items) => {
        let next = [...items];
        list.forEach((b) => {
          const layout = defaultPieceLayout(b.kind, next.length);
          next = [...next, { id: uid("w"), layout, ...b }];
        });
        return next;
      });
    }
    setEditorOpen(false);
    setEditingPiece(null);
  };

  // Re-materialize a piece from its stored source view and re-freeze the result.
  const handleRefreshPiece = async (piece) => {
    if (!piece?.sourceRef?.state) return;
    setRefreshingId(piece.id);
    try {
      const s = deserializeView(piece.sourceRef.state);
      const { pivot, meta } = await materializeView(s, {
        dimByKey,
        labels: labelsRef.current,
      });
      const used = [...(s.assignments.rows || []), ...(s.assignments.cols || [])];
      const fieldTitles = Object.fromEntries(used.map((k) => [k, fieldTitle(k)]));
      const result = freezePieceResult(pivot, {
        kind: piece.kind,
        chartType: s.chartType,
        seriesKey: s.seriesKey,
        valueMode: s.valueMode,
        title: piece.title,
        fieldTitles,
        metricKey: piece.sourceRef?.metricKey,
        metricLabel: piece.sourceRef?.metricLabel,
      });
      updateCurrentItems((items) =>
        items.map((it) =>
          it.id === piece.id
            ? {
                ...it,
                result,
                sourceRef: {
                  ...it.sourceRef,
                  capturedAt: new Date().toISOString(),
                  reportsTotal: meta?.total ?? pivot?.grandTotal,
                },
              }
            : it
        )
      );
    } catch (e) {
      console.error("Refresh piece failed", e);
    } finally {
      setRefreshingId(null);
    }
  };

  const handleAddSlide = () => {
    patchSlides((prev) => [...prev, emptySlide(`Slide ${prev.length + 1}`)]);
    setSlideIdx(slides.length);
  };

  const handleRemoveSlide = () => {
    if (slides.length <= 1) return;
    if (!window.confirm("¿Eliminar este slide y sus piezas?")) return;
    patchSlides((prev) => prev.filter((_, i) => i !== slideIdx));
    setSlideIdx((i) => Math.max(0, i - 1));
  };

  const handleSave = () => {
    const content = { version: 1, layout: type, slides };
    update.mutate(
      { id, name: name.trim() || "BI Panel", type, content },
      { onSuccess: () => setDirty(false) }
    );
  };

  if (isLoading) return <Box sx={{ p: 3 }}>Cargando…</Box>;
  if (!data) return <Box sx={{ p: 3, color: "error.main" }}>No se encontró el dashboard.</Box>;

  return (
    <Box sx={{ p: 2 }}>
      <SectionHeader
        title="Editar BI Panel"
        subtitle="Agrega piezas; cada una guarda su resultado congelado."
        icon={<Icon name="bold_element_2" size={48} />}
        component={
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {dirty && <Chip size="small" color="warning" label="Sin guardar" />}
            <Button
              size="small"
              variant="text"
              sx={{ fontSize: "0.75rem" }}
              onClick={() => navigate(`/analytics/custom-dashboards/${id}`)}
            >
              Ver
            </Button>
            <Button
              size="small"
              variant="contained"
              sx={{ fontSize: "0.75rem" }}
              onClick={handleSave}
              disabled={update.isPending}
            >
              Guardar
            </Button>
          </Box>
        }
      />

      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 2, flexWrap: "wrap" }}>
        <TextField
          label="Nombre"
          size="small"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setDirty(true);
          }}
          sx={{ minWidth: 240 }}
        />
        <Button
          size="small"
          variant="outlined"
          startIcon={<Icon name="bold_add_square" size={16} />}
          onClick={handleAddPiece}
        >
          Agregar pieza
        </Button>
      </Box>

      {isSlides && (
        <Box sx={{ display: "flex", alignItems: "center", mt: 2, borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={Math.min(slideIdx, slides.length - 1)}
            onChange={(_e, v) => setSlideIdx(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {slides.map((s, i) => (
              <Tab key={s.id} label={s.title || `Slide ${i + 1}`} />
            ))}
          </Tabs>
          <Tooltip title="Agregar slide">
            <IconButton onClick={handleAddSlide} size="small">
              <Icon name="bold_add_square" size={18} />
            </IconButton>
          </Tooltip>
          {slides.length > 1 && (
            <Tooltip title="Eliminar slide">
              <IconButton onClick={handleRemoveSlide} size="small">
                <Icon name="bold_trash" size={18} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      <Box sx={{ mt: 2 }}>
        {!current || current.items.length === 0 ? (
          <Box
            className="dyTheme1 dyBorder1 rounded"
            sx={{ p: 4, textAlign: "center", color: "text.secondary", fontSize: "0.875rem" }}
          >
            Sin piezas todavía. Usa <strong>Agregar pieza</strong> para empezar.
          </Box>
        ) : (
          <DashboardGrid
            items={current.items}
            editable
            onLayoutChange={handleLayoutChange}
            onEditPiece={handleEditPiece}
            onRefreshPiece={handleRefreshPiece}
            onRemovePiece={handleRemovePiece}
            onRenamePiece={handleRenamePiece}
            refreshingId={refreshingId}
            maxRows={isSlides ? SLIDE_MAX_ROWS : undefined}
          />
        )}
      </Box>

      {editorOpen && (
        <PieceEditor
          open={editorOpen}
          initial={editingPiece}
          onClose={() => {
            setEditorOpen(false);
            setEditingPiece(null);
          }}
          onSave={handleSavePiece}
        />
      )}
    </Box>
  );
};

export default CustomDashboardBuilder;
